import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SET } from "@/constants/testIds";
import { useAppearance } from "@/context/AppearanceContext";
import { useAuth } from "@/context/AuthContext";
import { canWriteMainTab, canAccessMainTab } from "@/lib/maintabAccess";

export default function SettingsPage() {
  const { enabled, color, presets, setEnabled, selectColor } = useAppearance();
  const { user, isAdmin } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "settings");
  const canOpenReportDesigner = canAccessMainTab(user?.maintabAccess, "generic-report-designer", { isAdmin });

  return (
    <div data-testid={SET.page} className="space-y-6">
      <div>
        <h1 data-testid={SET.title} className="text-3xl font-semibold tracking-tight text-slate-900">
          Settings
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Appearance preferences and configurable terminology defaults
        </p>
      </div>

      <section
        data-testid={SET.appearanceSection}
        className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">Appearance</h2>
            <p className="mt-1 text-sm text-slate-500">
              Page Background applies a preset color to EMR page canvases. Cards and panels stay white.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Label htmlFor={SET.pageBackgroundSwitch} className="text-sm text-slate-700">
              Page Background
            </Label>
            <Switch
              id={SET.pageBackgroundSwitch}
              data-testid={SET.pageBackgroundSwitch}
              checked={enabled}
              onCheckedChange={canWrite ? setEnabled : undefined}
              disabled={!canWrite}
              className="data-[state=checked]:bg-teal-700"
            />
          </div>
        </div>

        {enabled ? (
          <div
            data-testid={SET.pageBackgroundSwatches}
            className="mt-5 flex flex-wrap gap-4"
          >
            {presets.map((preset) => {
              const selected = color === preset.color;
              return (
                <button
                  key={preset.id}
                  type="button"
                  data-testid={`${SET.pageBackgroundSwatch}-${preset.id}`}
                  onClick={canWrite ? () => selectColor(preset.color) : undefined}
                  disabled={!canWrite}
                  className="flex w-[4.5rem] flex-col items-center gap-2"
                  aria-pressed={selected}
                  aria-label={`${preset.name} page background`}
                >
                  <span
                    className={`h-9 w-9 rounded-full border border-slate-200 shadow-sm ${
                      selected ? "ring-2 ring-[#0b1f33] ring-offset-2" : ""
                    }`}
                    style={{ backgroundColor: preset.color }}
                  />
                  <span className="text-xs font-medium text-slate-600">{preset.name}</span>
                </button>
              );
            })}
          </div>
        ) : null}
      </section>

      <section
        data-testid={SET.masterDataSection}
        className="space-y-4"
      >
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Master Data</h2>
          <p className="mt-1 text-sm text-slate-500">
            System terminology catalogs. Separate from individual patient clinical records.
          </p>
        </div>

        <div
          data-testid={SET.pxMasterSection}
          className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
        >
          <h3 className="text-sm font-semibold text-slate-900">Px Master</h3>
          <p className="mt-1 text-sm text-slate-500">
            Central FHIR-compatible procedure terminology. Surgical History Quick Entry and
            available procedures are configured here rather than hard-coded in the patient chart.
          </p>
          {isAdmin ? (
            <Link
              to="/settings/px-master"
              data-testid={SET.pxMasterLink}
              className="mt-4 inline-flex text-sm font-medium text-teal-700 hover:underline"
            >
              Manage Px Master →
            </Link>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              Only administrators can modify Px Master. Clinicians can use concepts on Surgical History.
            </p>
          )}
        </div>

        <div
          data-testid={SET.allergenMasterSection}
          className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
        >
          <h3 className="text-sm font-semibold text-slate-900">Allergen Master</h3>
          <p className="mt-1 text-sm text-slate-500">
            Complete allergen / contraindication catalog used by Allergy Lookup. Patient
            allergy records reference these concepts and keep their own snapshots.
          </p>
          {isAdmin ? (
            <Link
              to="/settings/allergen-master"
              data-testid={SET.allergenMasterLink}
              className="mt-4 inline-flex text-sm font-medium text-teal-700 hover:underline"
            >
              Manage Allergen Master →
            </Link>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              Only administrators can modify Allergen Master. Clinicians search active
              concepts when recording patient allergies.
            </p>
          )}
        </div>
      </section>

      {canOpenReportDesigner ? (
        <section
          data-testid={SET.genericReportDesignerSection}
          className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-slate-900">Generic Report Designer</h2>
          <p className="mt-1 text-sm text-slate-500">
            Experimental system-level header and footer template for generated reports.
            One Generic Report Template applies to all note types.
          </p>
          <Link
            to="/settings/generic-report-designer"
            data-testid={SET.genericReportDesignerLink}
            className="mt-4 inline-flex text-sm font-medium text-teal-700 hover:underline"
          >
            Open Generic Report Designer →
          </Link>
        </section>
      ) : null}

      {isAdmin ? (
        <section
          data-testid={SET.systemChangeHistorySection}
          className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-slate-900">System Change History</h2>
          <p className="mt-1 text-sm text-slate-500">
            Admin-only ledger of meaningful product milestones. Not a Git commit log.
          </p>
          <Link
            to="/admin/system-change-history"
            data-testid={SET.systemChangeHistoryLink}
            className="mt-4 inline-flex text-sm font-medium text-teal-700 hover:underline"
          >
            Open System Change History →
          </Link>
        </section>
      ) : null}

      {isAdmin ? (
        <section
          data-testid={SET.accessControlSection}
          className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-slate-900">Access Control</h2>
          <p className="mt-1 text-sm text-slate-500">
            Organization default and individual user subtab access. Administrators only.
          </p>
          <Link
            to="/admin/access-control"
            data-testid={SET.accessControlLink}
            className="mt-4 inline-flex text-sm font-medium text-teal-700 hover:underline"
          >
            Open Access Control →
          </Link>
        </section>
      ) : null}

      {isAdmin ? (
        <section
          data-testid={SET.smsSandboxSection}
          className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-5 shadow-sm"
        >
          <h2 className="text-sm font-semibold text-slate-900">SMS Sandbox Test</h2>
          <p className="mt-1 text-sm text-slate-500">
            Administrator-only AWS End User Messaging SMS sandbox check. Not patient messaging.
          </p>
          <Link
            to="/admin/sms-sandbox-test"
            data-testid={SET.smsSandboxLink}
            className="mt-4 inline-flex text-sm font-medium text-teal-700 hover:underline"
          >
            Open SMS Sandbox Test →
          </Link>
        </section>
      ) : null}
    </div>
  );
}
